import React, {useEffect, useMemo, useState} from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider, ListItemButton, FormControlLabel, Switch, Theme, useTheme, Avatar,
} from '@mui/material';
import {
    Menu as MenuIcon,
    AccountCircle,
    Dashboard,
    Person,
    School,
    Work,
    Build,
    CardMembership,
    Folder,
    Share,
    Language,
} from '@mui/icons-material';
import {useNavigate, useLocation} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import LanguageSelector from "./LanguageSelector";
import {useTranslation} from "react-i18next";
import {CvVersionAPI} from "../services/API/CvVersionAPI";
import {PersonalDataAPI} from "../services/API/PersonalDataAPI";

interface NavigationProps {
    onToggleDarkMode: () => void;
}

const Navigation: React.FC<NavigationProps> = ({onToggleDarkMode}) => {
    const {user, logout} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [imgUrl, setImgUrl] = useState<string | null>(null);

    // translate
    const {t} = useTranslation();

    useEffect(() => {
        loadImgUrl();
    }, [user?.id]);

    const loadImgUrl = async () => {
        if (!user) return; // guard esplicita
        try {
            const url = await PersonalDataAPI.Instance<PersonalDataAPI>().getUrlPhoto();
            setImgUrl(url);
        } catch (error: any) {
            if (error?.response?.status !== 401 && error?.response?.status !== 404) {
                console.error('Error loading data:', error);
            }
            setImgUrl(null);
        }
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        handleClose();
    };

    const menuItems = [
        {label: t('navigation.dashboard'), path: '/dashboard', icon: <Dashboard/>},
        {label: t('navigation.personalData'), path: '/personal-data', icon: <Person/>},
        {label: t('navigation.education'), path: '/education', icon: <School/>},
        {label: t('navigation.workExperience'), path: '/experience', icon: <Work/>},
        {label: t('navigation.skill'), path: '/skills', icon: <Build/>},
        {label: t('navigation.certification'), path: '/certifications', icon: <CardMembership/>},
        {label: t('navigation.project'), path: '/projects', icon: <Folder/>},
        {label: t('navigation.language'), path: '/languages', icon: <Language/>},
        {label: t('navigation.share'), path: '/shares', icon: <Share/>},
        {label: t('userMenu.subscription'), path: '/subscription', icon: <CardMembership/>},
    ];

    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';

    const handleThemeToggle = () => {
        onToggleDarkMode();
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <>
            <AppBar position="sticky">
                <Toolbar>
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={() => setDrawerOpen(true)}
                        sx={{mr: 2}}
                    >
                        <MenuIcon/>
                    </IconButton>

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{flexGrow: 1, cursor: 'pointer'}}
                        onClick={() => navigate('/')}
                    >
                        MyCVitae
                    </Typography>

                    {user ? (
                        <Box>
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                color="inherit"
                            >
                                <Avatar
                                    src={imgUrl ?? undefined}
                                    sx={{ width: 32, height: 32 }}
                                >
                                    {/* Mostrato solo se src è assente o non carica */}
                                    <AccountCircle />
                                </Avatar>
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                <MenuItem disabled>
                                    <Typography variant="body2">
                                        {user.username} {user.isPremium && '(Premium)'}
                                    </Typography>
                                </MenuItem>
                                <Divider/>
                                <MenuItem onClick={() => {
                                    navigate('/profile');
                                    handleClose();
                                }}>
                                    {t('userMenu.profile')}
                                </MenuItem>
                                <MenuItem onClick={() => {
                                    navigate('/subscription');
                                    handleClose();
                                }}>
                                    {t('userMenu.subscription')}
                                </MenuItem>
                                <MenuItem>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={darkMode}
                                                onChange={handleThemeToggle}
                                                color="primary"
                                            />
                                        }
                                        label={t('userMenu.darkMode')}/>
                                </MenuItem>
                                <MenuItem>
                                    <LanguageSelector/>
                                </MenuItem>
                                <Divider/>
                                <MenuItem onClick={handleLogout}>
                                    {t('userMenu.logout')}
                                </MenuItem>
                            </Menu>
                        </Box>
                    ) : (
                        <Box>
                            <Button color="inherit" onClick={() => navigate('/login')}>
                                {t('userMenu.login')}
                            </Button>
                            <Button color="inherit" onClick={() => navigate('/register')}>
                                {t('userMenu.signIn')}
                            </Button>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            >
                <Box sx={{width: 250}}>
                    <Toolbar>
                        <Typography variant="h6" noWrap component="div">
                            {t('userMenu.menu')}
                        </Typography>
                    </Toolbar>
                    <Divider/>
                    <List>
                        {menuItems.map((item) => (
                            <ListItemButton
                                key={item.label}
                                onClick={() => {
                                    navigate(item.path);
                                    setDrawerOpen(false);
                                }}
                                selected={isActive(item.path)}
                            >
                                <ListItemIcon>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.label}/>
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

export default Navigation;
